import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Package, 
  ShoppingCart, 
  Users, 
  UserCheck, 
  FileText, 
  DollarSign,
  TrendingUp,
  Calendar,
  ChevronDown,
  Building,
  CreditCard,
  Wallet,
  ShoppingBag,
  Receipt,
  Award
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { DashboardStats, MonthlyReportData } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import SubscriptionInvoiceGraph from '../components/SubscriptionInvoiceGraph';
import MRRGraph from '../components/MRRGraph';
import SalesGraph from '../components/SalesGraph';
import UsersGraph from '../components/UsersGraph';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [showInvoiceGraph, setShowInvoiceGraph] = useState(false);
  const [showMRRGraph, setShowMRRGraph] = useState(false);
  const [showSalesGraph, setShowSalesGraph] = useState(false);
  const [showUsersGraph, setShowUsersGraph] = useState(false);

  // Monthly Report states
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReportData | null>(null);
  const [monthlyReportLoading, setMonthlyReportLoading] = useState(false);
  const [monthlyReportError, setMonthlyReportError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [showMonthlyReport, setShowMonthlyReport] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, [selectedDate]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getDashboardStats(selectedDate);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setError('Failed to load dashboard statistics');
      // Mock data for development
      setStats({
        productsCreated: 1247,
        salesCreated: 8934,
        branchesCreated: 156,
        usersRegistered: 2341,
        activeUsers: 1876,
        subscriptionInvoiceCount: 89,
        subscriptionInvoiceTotal: 45600000,
        storeInvoiceCount: 42,
        storeInvoiceTotal: 18900000,
        dominateCount: 17,
        mrr: 12500000
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('id-ID').format(num);
  };

  const loadMonthlyReport = async () => {
    try {
      setMonthlyReportLoading(true);
      setMonthlyReportError(null);
      const period = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      const response = await api.getMonthlyReport(period);
      setMonthlyReport(response.data);
    } catch (error) {
      console.error('Error loading monthly report:', error);
      setMonthlyReportError('Failed to load monthly report');
      // Mock data for development
      setMonthlyReport({
        period: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`,
        currentMonth: {
          month: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`,
          monthName: new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' }),
          companies: {
            totalRegistered: 145,
            withSalesInPeriod: 68,
            withPaidInvoicesInMonth: 21,
            accumulatedRegistered: 944,
            accumulatedWithPaidInvoices: 133
          },
          subscriptions: {
            launch: {
              totalInvoices: 25,
              totalValue: 12500000,
              totalDiscount: 500000,
              totalProgramDiscount: 250000,
              totalAllDiscounts: 750000
            },
            elevate: {
              totalInvoices: 18,
              totalValue: 36000000,
              totalDiscount: 1200000,
              totalProgramDiscount: 600000,
              totalAllDiscounts: 1800000
            },
            dominate: {
              totalInvoices: 9,
              totalValue: 27000000,
              totalDiscount: 0,
              totalProgramDiscount: 0,
              totalAllDiscounts: 0
            },
            totalBought: 43
          },
          paymentGateways: {
            tripay: {
              totalInvoices: 35,
              totalValue: 40000000,
              launchValue: 12000000,
              elevateValue: 28000000,
              dominateValue: 20000000,
              gatewayFee: 800000,
              netValue: 39200000
            },
            appstoreIAP: {
              totalInvoices: 8,
              totalValue: 8500000,
              launchValue: 500000,
              elevateValue: 8000000,
              dominateValue: 7000000,
              gatewayFee: 1275000,
              netValue: 7225000
            }
          }
        },
        previousMonth: {
          month: `${selectedYear}-${String(selectedMonth === 1 ? 12 : selectedMonth - 1).padStart(2, '0')}`,
          monthName: new Date(selectedYear, selectedMonth - 2).toLocaleString('en-US', { month: 'long', year: 'numeric' }),
          companies: {
            totalRegistered: 138,
            withSalesInPeriod: 62,
            withPaidInvoicesInMonth: 18,
            accumulatedRegistered: 899,
            accumulatedWithPaidInvoices: 112
          },
          subscriptions: {
            launch: {
              totalInvoices: 22,
              totalValue: 11000000,
              totalDiscount: 400000,
              totalProgramDiscount: 200000,
              totalAllDiscounts: 600000
            },
            elevate: {
              totalInvoices: 16,
              totalValue: 32000000,
              totalDiscount: 1000000,
              totalProgramDiscount: 500000,
              totalAllDiscounts: 1500000
            },
            dominate: {
              totalInvoices: 7,
              totalValue: 21000000,
              totalDiscount: 0,
              totalProgramDiscount: 0,
              totalAllDiscounts: 0
            },
            totalBought: 38
          },
          paymentGateways: {
            tripay: {
              totalInvoices: 32,
              totalValue: 37000000,
              launchValue: 10500000,
              elevateValue: 26500000,
              dominateValue: 15000000,
              gatewayFee: 740000,
              netValue: 36260000
            },
            appstoreIAP: {
              totalInvoices: 6,
              totalValue: 6000000,
              launchValue: 500000,
              elevateValue: 5500000,
              dominateValue: 6000000,
              gatewayFee: 900000,
              netValue: 5100000
            }
          }
        }
      });
    } finally {
      setMonthlyReportLoading(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const handleExportReport = async () => {
    try {
      setExportingReport(true);
      const period = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      const blob = await api.exportMonthlyReport(period);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `monthly_report_${period}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExportingReport(false);
    }
  };

  const dashboardStats = [
    { 
      name: 'Products Created', 
      value: stats ? formatNumber(stats.productsCreated) : '0', 
      icon: Package, 
      href: '/companies', 
      color: 'bg-blue-500',
      description: 'Total products created'
    },
    { 
      name: 'Sales Created', 
      value: stats ? formatNumber(stats.salesCreated) : '0', 
      icon: ShoppingCart, 
      href: '/companies', 
      color: 'bg-green-500',
      description: 'Total sales transactions',
      onClick: () => setShowSalesGraph(true)
    },
    { 
      name: 'Branches Created', 
      value: stats ? formatNumber(stats.branchesCreated) : '0', 
      icon: Building2, 
      href: '/companies', 
      color: 'bg-purple-500',
      description: 'Total company branches'
    },
    { 
      name: 'Users Registered', 
      value: stats ? formatNumber(stats.usersRegistered) : '0', 
      icon: Users, 
      href: '/companies', 
      color: 'bg-indigo-500',
      description: 'Total registered users',
      onClick: () => setShowUsersGraph(true)
    },
    { 
      name: 'Active Users', 
      value: stats ? formatNumber(stats.activeUsers) : '0', 
      icon: UserCheck, 
      href: '/companies', 
      color: 'bg-emerald-500',
      description: 'Currently active users',
      onClick: () => setShowUsersGraph(true)
    },
    { 
      name: 'Subscription Invoices', 
      value: stats ? formatNumber(stats.subscriptionInvoiceCount) : '0', 
      icon: FileText, 
      href: '/subscription-invoices', 
      color: 'bg-orange-500',
      description: 'Total subscription invoices',
      onClick: () => setShowInvoiceGraph(true)
    },
    { 
      name: 'Invoice Total', 
      value: stats ? formatCurrency(stats.subscriptionInvoiceTotal) : 'Rp 0', 
      icon: DollarSign, 
      href: '/subscription-invoices', 
      color: 'bg-yellow-500',
      description: 'Total invoice amount',
      onClick: () => setShowInvoiceGraph(true)
    },
    {
      name: 'Store Invoice (Lunas)',
      value: stats ? formatNumber(stats.storeInvoiceCount) : '0',
      icon: ShoppingBag,
      href: '/store-invoices',
      color: 'bg-teal-500',
      description: 'Total paid store invoices'
    },
    {
      name: 'Total Store Invoice',
      value: stats ? formatCurrency(stats.storeInvoiceTotal) : 'Rp 0',
      icon: Receipt,
      href: '/store-invoices',
      color: 'bg-cyan-500',
      description: 'Total store invoice amount'
    },
    {
      name: 'Langganan Dominate (Lv 3)',
      value: stats ? formatNumber(stats.dominateCount) : '0',
      icon: Award,
      href: '/companies',
      color: 'bg-pink-500',
      description: 'Companies on Dominate plan'
    },
    {
      name: 'Monthly Recurring Revenue',
      value: stats ? formatCurrency(stats.mrr) : 'Rp 0', 
      icon: TrendingUp, 
      href: '/subscriptions', 
      color: 'bg-red-500',
      description: 'Current MRR',
      onClick: () => setShowMRRGraph(true)
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" text="Loading dashboard statistics..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-600">{error}</div>
        <button
          onClick={loadDashboardStats}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-2 text-sm text-gray-600">
              Welcome to Kloa Admin Panel. Here's an overview of your platform statistics.
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <label htmlFor="date-picker" className="block text-sm font-medium text-gray-700 mb-2">
              Select Date
            </label>
            <div className="flex items-center space-x-2">
              <input
                id="date-picker"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                className="px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Today
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {dashboardStats.map((stat) => {
          const hasOnClick = (stat as any).onClick;
          
          if (hasOnClick) {
            return (
              <button
                key={stat.name}
                onClick={(stat as any).onClick}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200 w-full text-left"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 rounded-md p-3 ${stat.color}`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {stat.name}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stat.value}
                        </dd>
                        <dd className="text-xs text-gray-400 mt-1">
                          {stat.description}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </button>
            );
          }
          
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200 w-full text-left"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-md p-3 ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </dd>
                      <dd className="text-xs text-gray-400 mt-1">
                        {stat.description}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Summary Cards */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Key Metrics */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Key Metrics
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                      <UserCheck className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">User Activity Rate</p>
                    <p className="text-sm text-gray-500">Active vs Registered Users</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-gray-900">
                    {stats ? Math.round((stats.activeUsers / stats.usersRegistered) * 100) : 0}%
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Average Invoice Value</p>
                    <p className="text-sm text-gray-500">Revenue per subscription</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-gray-900">
                    {stats && stats.subscriptionInvoiceCount > 0 
                      ? formatCurrency(stats.subscriptionInvoiceTotal / stats.subscriptionInvoiceCount)
                      : 'Rp 0'
                    }
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Branches per Company</p>
                    <p className="text-sm text-gray-500">Average branch count</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-gray-900">
                    {stats ? Math.round(stats.branchesCreated / 10) : 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <Link
                to="/companies"
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <span>Manage Companies</span>
                <Building2 className="h-4 w-4" />
              </Link>
              
              <Link
                to="/subscriptions"
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <span>View Subscriptions</span>
                <FileText className="h-4 w-4" />
              </Link>
              
              <Link
                to="/subscription-invoices"
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <span>Subscription Invoices</span>
                <DollarSign className="h-4 w-4" />
              </Link>
              
              <Link
                to="/marketing-programs"
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <span>Marketing Programs</span>
                <TrendingUp className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Invoice Graph Modal */}
      <SubscriptionInvoiceGraph
        isVisible={showInvoiceGraph}
        onClose={() => setShowInvoiceGraph(false)}
      />

      {/* MRR Graph Modal */}
      <MRRGraph
        isVisible={showMRRGraph}
        onClose={() => setShowMRRGraph(false)}
      />

      {/* Sales Graph Modal */}
      <SalesGraph
        isVisible={showSalesGraph}
        onClose={() => setShowSalesGraph(false)}
      />

      {/* Users Graph Modal */}
      <UsersGraph
        isVisible={showUsersGraph}
        onClose={() => setShowUsersGraph(false)}
      />

      {/* Monthly Report Section */}
      <div className="mt-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Header with Toggle */}
          <button
            onClick={() => {
              if (!showMonthlyReport) {
                loadMonthlyReport();
              }
              setShowMonthlyReport(!showMonthlyReport);
            }}
            className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Calendar className="h-6 w-6 text-white" />
              <h2 className="text-lg font-semibold text-white">Monthly Report</h2>
            </div>
            <ChevronDown className={`h-5 w-5 text-white transition-transform duration-200 ${showMonthlyReport ? 'rotate-180' : ''}`} />
          </button>

          {/* Collapsible Content */}
          {showMonthlyReport && (
            <div className="p-6">
              {/* Filters */}
              <div className="flex flex-wrap items-end gap-4 mb-6 pb-6 border-b border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="block w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={loadMonthlyReport}
                  disabled={monthlyReportLoading}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {monthlyReportLoading ? 'Loading...' : 'Generate Report'}
                </button>
                <button
                  onClick={handleExportReport}
                  disabled={exportingReport}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{exportingReport ? 'Exporting...' : 'Export Excel'}</span>
                </button>
              </div>

              {/* Error State */}
              {monthlyReportError && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-red-600">{monthlyReportError}</div>
                  <button
                    onClick={loadMonthlyReport}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                </div>
              )}

              {/* Report Content */}
              {monthlyReport && !monthlyReportLoading && (
                <div className="space-y-8">
                  {/* Period Header */}
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {monthlyReport.currentMonth.monthName}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Compared to {monthlyReport.previousMonth.monthName}
                    </p>
                  </div>

                  {/* Company Statistics */}
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Building className="h-5 w-5 text-blue-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Company Statistics</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Period Stats */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Registered (Month)</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {formatNumber(monthlyReport.currentMonth.companies.totalRegistered)}
                        </p>
                        <div className="flex items-center mt-2">
                          <span className={`text-sm font-medium ${calculateGrowth(monthlyReport.currentMonth.companies.totalRegistered, monthlyReport.previousMonth.companies.totalRegistered) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.companies.totalRegistered, monthlyReport.previousMonth.companies.totalRegistered) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.companies.totalRegistered, monthlyReport.previousMonth.companies.totalRegistered)}%
                          </span>
                          <span className="text-xs text-gray-500 ml-1">vs last month</span>
                        </div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Trial (Month)</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {formatNumber(monthlyReport.currentMonth.companies.withSalesInPeriod)}
                        </p>
                        <div className="flex items-center mt-2">
                          <span className={`text-sm font-medium ${calculateGrowth(monthlyReport.currentMonth.companies.withSalesInPeriod, monthlyReport.previousMonth.companies.withSalesInPeriod) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.companies.withSalesInPeriod, monthlyReport.previousMonth.companies.withSalesInPeriod) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.companies.withSalesInPeriod, monthlyReport.previousMonth.companies.withSalesInPeriod)}%
                          </span>
                          <span className="text-xs text-gray-500 ml-1">vs last month</span>
                        </div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Subscribing (Month)</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {formatNumber(monthlyReport.currentMonth.companies.withPaidInvoicesInMonth)}
                        </p>
                        <div className="flex items-center mt-2">
                          <span className={`text-sm font-medium ${calculateGrowth(monthlyReport.currentMonth.companies.withPaidInvoicesInMonth, monthlyReport.previousMonth.companies.withPaidInvoicesInMonth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.companies.withPaidInvoicesInMonth, monthlyReport.previousMonth.companies.withPaidInvoicesInMonth) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.companies.withPaidInvoicesInMonth, monthlyReport.previousMonth.companies.withPaidInvoicesInMonth)}%
                          </span>
                          <span className="text-xs text-gray-500 ml-1">vs last month</span>
                        </div>
                      </div>
                    </div>
                    {/* Accumulated Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                        <p className="text-sm text-indigo-600">Accumulated Registered</p>
                        <p className="text-3xl font-bold text-indigo-900 mt-1">
                          {formatNumber(monthlyReport.currentMonth.companies.accumulatedRegistered)}
                        </p>
                        <p className="text-xs text-indigo-500 mt-1">Total registered companies to date</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                        <p className="text-sm text-orange-600">Accumulated Subscribed</p>
                        <p className="text-3xl font-bold text-orange-900 mt-1">
                          {formatNumber(monthlyReport.currentMonth.companies.accumulatedWithPaidInvoices)}
                        </p>
                        <p className="text-xs text-orange-500 mt-1">Total companies that subscribed to date</p>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Statistics */}
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <CreditCard className="h-5 w-5 text-indigo-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Subscription Statistics</h4>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                      {/* Launch Package */}
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Launch Package</h5>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateGrowth(monthlyReport.currentMonth.subscriptions.launch.totalInvoices, monthlyReport.previousMonth.subscriptions.launch.totalInvoices) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.launch.totalInvoices, monthlyReport.previousMonth.subscriptions.launch.totalInvoices) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.launch.totalInvoices, monthlyReport.previousMonth.subscriptions.launch.totalInvoices)}%
                          </span>
                        </div>
                        <div className="mt-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Invoices</span>
                            <div className="text-right">
                              <span className="text-sm font-semibold text-gray-900">
                                {formatNumber(monthlyReport.currentMonth.subscriptions.launch.totalInvoices)}
                              </span>
                              <span className="text-xs text-gray-400 ml-2">
                                (was {formatNumber(monthlyReport.previousMonth.subscriptions.launch.totalInvoices)})
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Subtotal</span>
                            <span className="text-sm font-semibold text-gray-900">
                              {formatCurrency(monthlyReport.currentMonth.subscriptions.launch.totalValue + monthlyReport.currentMonth.subscriptions.launch.totalAllDiscounts)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Discounts</span>
                            <span className="text-sm font-semibold text-red-600">
                              -{formatCurrency(monthlyReport.currentMonth.subscriptions.launch.totalAllDiscounts)}
                            </span>
                          </div>
                          <div className="pt-3 border-t border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Grand Total</span>
                              <div className="text-right">
                                <span className="text-lg font-bold text-primary-600">
                                  {formatCurrency(monthlyReport.currentMonth.subscriptions.launch.totalValue)}
                                </span>
                                <span className="text-xs text-gray-400 ml-2">
                                  (was {formatCurrency(monthlyReport.previousMonth.subscriptions.launch.totalValue)})
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Elevate Package */}
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Elevate Package</h5>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateGrowth(monthlyReport.currentMonth.subscriptions.elevate.totalInvoices, monthlyReport.previousMonth.subscriptions.elevate.totalInvoices) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.elevate.totalInvoices, monthlyReport.previousMonth.subscriptions.elevate.totalInvoices) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.elevate.totalInvoices, monthlyReport.previousMonth.subscriptions.elevate.totalInvoices)}%
                          </span>
                        </div>
                        <div className="mt-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Invoices</span>
                            <div className="text-right">
                              <span className="text-sm font-semibold text-gray-900">
                                {formatNumber(monthlyReport.currentMonth.subscriptions.elevate.totalInvoices)}
                              </span>
                              <span className="text-xs text-gray-400 ml-2">
                                (was {formatNumber(monthlyReport.previousMonth.subscriptions.elevate.totalInvoices)})
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Subtotal</span>
                            <span className="text-sm font-semibold text-gray-900">
                              {formatCurrency(monthlyReport.currentMonth.subscriptions.elevate.totalValue + monthlyReport.currentMonth.subscriptions.elevate.totalAllDiscounts)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Discounts</span>
                            <span className="text-sm font-semibold text-red-600">
                              -{formatCurrency(monthlyReport.currentMonth.subscriptions.elevate.totalAllDiscounts)}
                            </span>
                          </div>
                          <div className="pt-3 border-t border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Grand Total</span>
                              <div className="text-right">
                                <span className="text-lg font-bold text-primary-600">
                                  {formatCurrency(monthlyReport.currentMonth.subscriptions.elevate.totalValue)}
                                </span>
                                <span className="text-xs text-gray-400 ml-2">
                                  (was {formatCurrency(monthlyReport.previousMonth.subscriptions.elevate.totalValue)})
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Dominate Package */}
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Dominate Package</h5>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateGrowth(monthlyReport.currentMonth.subscriptions.dominate.totalInvoices, monthlyReport.previousMonth.subscriptions.dominate.totalInvoices) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.dominate.totalInvoices, monthlyReport.previousMonth.subscriptions.dominate.totalInvoices) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.dominate.totalInvoices, monthlyReport.previousMonth.subscriptions.dominate.totalInvoices)}%
                          </span>
                        </div>
                        <div className="mt-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Invoices</span>
                            <div className="text-right">
                              <span className="text-sm font-semibold text-gray-900">
                                {formatNumber(monthlyReport.currentMonth.subscriptions.dominate.totalInvoices)}
                              </span>
                              <span className="text-xs text-gray-400 ml-2">
                                (was {formatNumber(monthlyReport.previousMonth.subscriptions.dominate.totalInvoices)})
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Subtotal</span>
                            <span className="text-sm font-semibold text-gray-900">
                              {formatCurrency(monthlyReport.currentMonth.subscriptions.dominate.totalValue + monthlyReport.currentMonth.subscriptions.dominate.totalAllDiscounts)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Discounts</span>
                            <span className="text-sm font-semibold text-red-600">
                              -{formatCurrency(monthlyReport.currentMonth.subscriptions.dominate.totalAllDiscounts)}
                            </span>
                          </div>
                          <div className="pt-3 border-t border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Grand Total</span>
                              <div className="text-right">
                                <span className="text-lg font-bold text-primary-600">
                                  {formatCurrency(monthlyReport.currentMonth.subscriptions.dominate.totalValue)}
                                </span>
                                <span className="text-xs text-gray-400 ml-2">
                                  (was {formatCurrency(monthlyReport.previousMonth.subscriptions.dominate.totalValue)})
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Total Summary */}
                      <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg p-5 text-white">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-sm font-medium text-primary-100 uppercase tracking-wide">Total Subscriptions</h5>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateGrowth(monthlyReport.currentMonth.subscriptions.totalBought, monthlyReport.previousMonth.subscriptions.totalBought) >= 0 ? 'bg-white/20 text-white' : 'bg-red-300 text-red-800'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.totalBought, monthlyReport.previousMonth.subscriptions.totalBought) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.subscriptions.totalBought, monthlyReport.previousMonth.subscriptions.totalBought)}%
                          </span>
                        </div>
                        <div className="mt-4 space-y-4">
                          <div>
                            <p className="text-3xl font-bold">{formatNumber(monthlyReport.currentMonth.subscriptions.totalBought)}</p>
                            <p className="text-sm text-primary-100">Total Bought (was {formatNumber(monthlyReport.previousMonth.subscriptions.totalBought)})</p>
                          </div>
                          <div className="pt-4 border-t border-primary-400">
                            <p className="text-2xl font-bold">
                              {formatCurrency(
                                monthlyReport.currentMonth.subscriptions.launch.totalValue +
                                monthlyReport.currentMonth.subscriptions.elevate.totalValue +
                                monthlyReport.currentMonth.subscriptions.dominate.totalValue
                              )}
                            </p>
                            <p className="text-sm text-primary-100">
                              Total Grand Total (was {formatCurrency(monthlyReport.previousMonth.subscriptions.launch.totalValue + monthlyReport.previousMonth.subscriptions.elevate.totalValue + monthlyReport.previousMonth.subscriptions.dominate.totalValue)})
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Gateway Statistics */}
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Wallet className="h-5 w-5 text-emerald-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Payment Gateway Statistics</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Tripay */}
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <CreditCard className="h-4 w-4 text-blue-600" />
                            </div>
                            <h5 className="text-lg font-medium text-gray-900">Tripay</h5>
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateGrowth(monthlyReport.currentMonth.paymentGateways.tripay.totalInvoices, monthlyReport.previousMonth.paymentGateways.tripay.totalInvoices) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.paymentGateways.tripay.totalInvoices, monthlyReport.previousMonth.paymentGateways.tripay.totalInvoices) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.paymentGateways.tripay.totalInvoices, monthlyReport.previousMonth.paymentGateways.tripay.totalInvoices)}%
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Total Invoices</p>
                            <p className="text-xl font-bold text-gray-900">
                              {formatNumber(monthlyReport.currentMonth.paymentGateways.tripay.totalInvoices)}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              was {formatNumber(monthlyReport.previousMonth.paymentGateways.tripay.totalInvoices)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Total Value</p>
                            <p className="text-xl font-bold text-gray-900">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.tripay.totalValue)}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.tripay.totalValue)}
                            </p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-xs text-blue-600">Launch Value</p>
                            <p className="text-lg font-semibold text-blue-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.tripay.launchValue)}
                            </p>
                            <p className="text-xs text-blue-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.tripay.launchValue)}
                            </p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3">
                            <p className="text-xs text-purple-600">Elevate Value</p>
                            <p className="text-lg font-semibold text-purple-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.tripay.elevateValue)}
                            </p>
                            <p className="text-xs text-purple-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.tripay.elevateValue)}
                            </p>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-3">
                            <p className="text-xs text-orange-600">Dominate Value</p>
                            <p className="text-lg font-semibold text-orange-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.tripay.dominateValue)}
                            </p>
                            <p className="text-xs text-orange-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.tripay.dominateValue)}
                            </p>
                          </div>
                          <div className="bg-red-50 rounded-lg p-3">
                            <p className="text-xs text-red-600">Payment Gateway Fee</p>
                            <p className="text-lg font-semibold text-red-700">
                              -{formatCurrency(monthlyReport.currentMonth.paymentGateways.tripay.gatewayFee)}
                            </p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3">
                            <p className="text-xs text-green-600">Net Value</p>
                            <p className="text-lg font-semibold text-green-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.tripay.netValue)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* AppStore IAP */}
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <Wallet className="h-4 w-4 text-gray-600" />
                            </div>
                            <h5 className="text-lg font-medium text-gray-900">AppStore IAP</h5>
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateGrowth(monthlyReport.currentMonth.paymentGateways.appstoreIAP.totalInvoices, monthlyReport.previousMonth.paymentGateways.appstoreIAP.totalInvoices) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {calculateGrowth(monthlyReport.currentMonth.paymentGateways.appstoreIAP.totalInvoices, monthlyReport.previousMonth.paymentGateways.appstoreIAP.totalInvoices) >= 0 ? '+' : ''}
                            {calculateGrowth(monthlyReport.currentMonth.paymentGateways.appstoreIAP.totalInvoices, monthlyReport.previousMonth.paymentGateways.appstoreIAP.totalInvoices)}%
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Total Invoices</p>
                            <p className="text-xl font-bold text-gray-900">
                              {formatNumber(monthlyReport.currentMonth.paymentGateways.appstoreIAP.totalInvoices)}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              was {formatNumber(monthlyReport.previousMonth.paymentGateways.appstoreIAP.totalInvoices)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Total Value</p>
                            <p className="text-xl font-bold text-gray-900">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.appstoreIAP.totalValue)}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.appstoreIAP.totalValue)}
                            </p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-xs text-blue-600">Launch Value</p>
                            <p className="text-lg font-semibold text-blue-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.appstoreIAP.launchValue)}
                            </p>
                            <p className="text-xs text-blue-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.appstoreIAP.launchValue)}
                            </p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3">
                            <p className="text-xs text-purple-600">Elevate Value</p>
                            <p className="text-lg font-semibold text-purple-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.appstoreIAP.elevateValue)}
                            </p>
                            <p className="text-xs text-purple-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.appstoreIAP.elevateValue)}
                            </p>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-3">
                            <p className="text-xs text-orange-600">Dominate Value</p>
                            <p className="text-lg font-semibold text-orange-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.appstoreIAP.dominateValue)}
                            </p>
                            <p className="text-xs text-orange-400 mt-1">
                              was {formatCurrency(monthlyReport.previousMonth.paymentGateways.appstoreIAP.dominateValue)}
                            </p>
                          </div>
                          <div className="bg-red-50 rounded-lg p-3">
                            <p className="text-xs text-red-600">Payment Gateway Fee</p>
                            <p className="text-lg font-semibold text-red-700">
                              -{formatCurrency(monthlyReport.currentMonth.paymentGateways.appstoreIAP.gatewayFee)}
                            </p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3">
                            <p className="text-xs text-green-600">Net Value</p>
                            <p className="text-lg font-semibold text-green-700">
                              {formatCurrency(monthlyReport.currentMonth.paymentGateways.appstoreIAP.netValue)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Manual (pameran / web) — tanpa biaya gateway */}
                      {monthlyReport.currentMonth.paymentGateways.manual && monthlyReport.previousMonth.paymentGateways.manual && (
                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-2">
                              <div className="h-8 w-8 bg-amber-100 rounded-full flex items-center justify-center">
                                <Receipt className="h-4 w-4 text-amber-600" />
                              </div>
                              <h5 className="text-lg font-medium text-gray-900">Manual (Pameran / Web)</h5>
                            </div>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateGrowth(monthlyReport.currentMonth.paymentGateways.manual.totalInvoices, monthlyReport.previousMonth.paymentGateways.manual.totalInvoices) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {calculateGrowth(monthlyReport.currentMonth.paymentGateways.manual.totalInvoices, monthlyReport.previousMonth.paymentGateways.manual.totalInvoices) >= 0 ? '+' : ''}
                              {calculateGrowth(monthlyReport.currentMonth.paymentGateways.manual.totalInvoices, monthlyReport.previousMonth.paymentGateways.manual.totalInvoices)}%
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500">Total Invoices</p>
                              <p className="text-xl font-bold text-gray-900">{formatNumber(monthlyReport.currentMonth.paymentGateways.manual.totalInvoices)}</p>
                              <p className="text-xs text-gray-400 mt-1">was {formatNumber(monthlyReport.previousMonth.paymentGateways.manual.totalInvoices)}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500">Total Value</p>
                              <p className="text-xl font-bold text-gray-900">{formatCurrency(monthlyReport.currentMonth.paymentGateways.manual.totalValue)}</p>
                              <p className="text-xs text-gray-400 mt-1">was {formatCurrency(monthlyReport.previousMonth.paymentGateways.manual.totalValue)}</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-xs text-blue-600">Launch Value</p>
                              <p className="text-lg font-semibold text-blue-700">{formatCurrency(monthlyReport.currentMonth.paymentGateways.manual.launchValue)}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3">
                              <p className="text-xs text-purple-600">Elevate Value</p>
                              <p className="text-lg font-semibold text-purple-700">{formatCurrency(monthlyReport.currentMonth.paymentGateways.manual.elevateValue)}</p>
                            </div>
                            <div className="bg-orange-50 rounded-lg p-3">
                              <p className="text-xs text-orange-600">Dominate Value</p>
                              <p className="text-lg font-semibold text-orange-700">{formatCurrency(monthlyReport.currentMonth.paymentGateways.manual.dominateValue)}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                              <p className="text-xs text-green-600">Net Value</p>
                              <p className="text-lg font-semibold text-green-700">{formatCurrency(monthlyReport.currentMonth.paymentGateways.manual.netValue)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {monthlyReportLoading && (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner size="lg" text="Loading monthly report..." />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
